import { getBroadcastRouteMatch } from 'utils/actionUtils';
import {
  updateSourceTranscodingStatus,
  setCanStartStream,
  updateEventDocUploadStatus,
  uploadSourceStatus,
} from '@shoflo/studio-redux/actions';

import {
  BROADCAST_UPDATE_STREAM_REALTIME,
  BROADCAST_BACKGROUND_SCAN,
  BROADCAST_DELETE_SOURCE,
  BROADCAST_UPLOAD_BACKGROUND,
} from '@shoflo/studio-redux/actions/types';

import { sendToDatadogWithIds } from '@shoflo/studio-redux/utils';

import { s3ProxyUploadStatuses, TranscodingStatuses } from '@studio/constants';

import { BACKGROUND_SCAN_FAILED_ERROR } from '@shoflo/studio-redux/constants/BackgroundUploadError';

function processRealTimeMessage({ dispatch, getState }, data) {
  const snsMessage = parseIfJSON(data);

  const { params } = getBroadcastRouteMatch(getState);

  const {
    params: { broadcastId, eventId, eventDocId },
  } = snsMessage;

  const isCurrentEvent = params.eventId === eventId;
  let { backgrounds, overlays, broadcastSources } = getState();

  /** Listen for eventDoc sns logs for transcoding update */
  if (isCurrentEvent) {
    const clientMethod = snsMessage.tags[2];

    /**
     * We only want to dispatch for the `eventDoc.updateTranscodingStatus` request
     * tags: ["event", "eventDoc", "client:eventDoc.updateTranscodingStatus"]
     */
    if (isValidClientMethod(clientMethod)) {
      if ('client:eventDoc.updateTranscodingStatus' === clientMethod) {
        const { responsePayload } = snsMessage;

        const payload = {
          eventDocId,
          transcodingStatus: responsePayload?.transcodingStatus,
        };

        dispatch(updateSourceTranscodingStatus(payload));
      } else if ('client:eventDoc.updateUploadStatus' === clientMethod) {
        // Try matching the returned eventDocId with backgrounds, followed by overlays, followed by broadcastSources
        let entity;
        if ((entity = matchEventDocId(backgrounds, eventDocId))) {
          // EventDocId matched with background therefore making the updates to upload the background
          if (
            snsMessage.payload &&
            snsMessage.payload.status === 'SCAN_SUCCESS'
          ) {
            // Update the background state to uploaded with correct details
            dispatch(
              updateEventDocUploadStatus(entity, BROADCAST_BACKGROUND_SCAN)
            );
            sendToDatadogWithIds(
              getState,
              'studio.s3Proxy.upload',
              `Background Scanned Successfully with eventDocId: ${eventDocId}`,
              false
            );
          }
          if (
            snsMessage.payload &&
            snsMessage.payload.status === 'SCAN_FAILED'
          ) {
            dispatch({
              type: `${BROADCAST_UPLOAD_BACKGROUND}_FAILURE`,
              payload: {
                _id: entity._id,
                message: BACKGROUND_SCAN_FAILED_ERROR,
              },
            });
          }
          return;
        }

        if ((entity = matchEventDocId(overlays, eventDocId))) {
          // Update the states of the overlay and dispatch action updates
          console.info('EventDoc found in overlays', entity);
          return;
        }

        if ((entity = createdBroadcastSource(broadcastSources, eventDocId))) {
          // Update the states of the broadcast sources and dispatch action updates
          if (
            snsMessage.payload &&
            snsMessage.payload.status === 'SCAN_SUCCESS' &&
            snsMessage.payload.location
          ) {
            dispatch(uploadSourceStatus(entity));
            sendToDatadogWithIds(
              getState,
              'studio.s3Proxy.upload',
              `Source Uploaded Successfully with eventDocId: ${eventDocId}`,
              false
            );
          } else if (snsMessage.payload.status === 'SCAN_FAILED') {
            dispatch({
              type: `${BROADCAST_DELETE_SOURCE}_SUCCESS`,
              payload: {
                _id: entity._id,
              },
            });
            sendToDatadogWithIds(
              getState,
              'studio.s3Proxy.upload',
              `Source Upload Failed for eventDocId: ${eventDocId}`,
              true
            );
          }
          return;
        }
      } else if ('client:eventDoc.updateMediaStatus' === clientMethod) {
        handleUpdateMediaStatus(
          snsMessage,
          broadcastSources,
          eventDocId,
          dispatch,
          getState
        );
      }
    }
  }

  /* Handle stream status updates */
  if (params.broadcastId === broadcastId) {
    const clientMethod = snsMessage.tags[1];
    /*
      will match the api request `broadcast.updateScene`, 'broadcast.createScene`,etc.
      We only want to dispatch this for the following requests:
      `broadcast.updateRecordingTaskState`,
      `broadcast.updateStreamEndpointTaskState` and `broadcast.updateScene`
    */
    if (isValidClientMethod(clientMethod)) {
      /** handle updates to recording and streaming task state */
      if (clientMethod === 'client:broadcast.stopStreams') {
        dispatch(setCanStartStream(false));
        sendToDatadogWithIds(
          getState,
          'processRealTimeMessage',
          `Stream is being stopped for broadcast`,
          false
        );
      }

      if (clientMethod === 'client:broadcast.updateStreamEndpointTaskState') {
        const { payload, responsePayload } = snsMessage;
        if (payload.taskState) {
          // if task state is stopped, remove the disabled button.
          if (payload.taskState === 'STOPPED') {
            // enable button stream again using redux action and state
            dispatch(setCanStartStream(true));
            sendToDatadogWithIds(
              getState,
              'processRealTimeMessage',
              `Stream stopped for broadcast`,
              false
            );
          }
          dispatch({
            type: `${BROADCAST_UPDATE_STREAM_REALTIME}`,
            payload: responsePayload.endpoint,
          });
        }
      }

      if (clientMethod === 'client:broadcast.updateStreamEndpoint') {
        const { responsePayload, payload } = snsMessage;
        dispatch({
          type: `${BROADCAST_UPDATE_STREAM_REALTIME}`,
          payload: responsePayload.endpoints.find(
            (endpoint) => endpoint._id === payload._id
          ),
        });
      }
    }
  }
}

// TODO: consolidate this function in WEBINAR-38206
function handleUpdateMediaStatus(
  snsMessage,
  broadcastSources,
  eventDocId,
  dispatch,
  getState
) {
  const { payload, responsePayload } = snsMessage;
  let entity;

  // Try matching the returned eventDocId with broadcastSources
  if ((entity = createdBroadcastSource(broadcastSources, eventDocId))) {
    if (payload?.status === s3ProxyUploadStatuses.ERROR) {
      if (responsePayload?.transcodingStatus) {
        const transcodePayload = {
          eventDocId,
          transcodingStatus: TranscodingStatuses.FAILED,
        };
        sendToDatadogWithIds(
          getState,
          'studio.uvs.upload',
          `Source transcoding failed for eventDocId: ${eventDocId}`,
          true
        );
        dispatch(updateSourceTranscodingStatus(transcodePayload));

        return;
      }
    }

    if (Object.values(s3ProxyUploadStatuses).includes(payload?.status)) {
      if (payload.status === s3ProxyUploadStatuses.SCAN_SUCCESS) {
        dispatch(uploadSourceStatus(entity));
        sendToDatadogWithIds(
          getState,
          'studio.uvs.upload',
          `Source Uploaded Successfully with eventDocId: ${eventDocId}`,
          false
        );
      } else if (
        payload.status === s3ProxyUploadStatuses.SCAN_FAILED ||
        payload.status === s3ProxyUploadStatuses.ERROR
      ) {
        dispatch({
          type: `${BROADCAST_DELETE_SOURCE}_SUCCESS`,
          payload: {
            _id: entity._id,
          },
        });
        sendToDatadogWithIds(
          getState,
          'studio.uvs.upload',
          `Source Upload Failed for eventDocId: ${eventDocId}`,
          true
        );
      }
    } else if (
      Object.values(TranscodingStatuses).includes(payload?.status) &&
      responsePayload?.transcodingStatus
    ) {
      const transcodePayload = {
        eventDocId,
        transcodingStatus: responsePayload.transcodingStatus,
      };
      dispatch(updateSourceTranscodingStatus(transcodePayload));
    }
  }
}

function matchEventDocId(sourcesData, eventDocId) {
  return sourcesData.find(
    (sourceData) => sourceData.source.value === eventDocId
  );
}

function createdBroadcastSource(broadcastSources, eventDocId) {
  return broadcastSources.sources.find(
    (broadcastSource) => broadcastSource.value.eventDocId === eventDocId
  );
}

function isValidClientMethod(clientMethod) {
  return clientMethod && typeof clientMethod === 'string';
}

function parseIfJSON(message) {
  try {
    // sns messages are delivered as json
    return JSON.parse(message);
  } catch (err) {
    // direct realtime messages are sent as a plain js object
    return message;
  }
}

export default processRealTimeMessage;
